import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  CalendarDays,
  Clapperboard,
  Clock3,
  Copy,
  Eye,
  EyeOff,
  FileVideo,
  Film,
  Globe,
  ImageOff,
  Languages,
  LayoutGrid,
  Layers,
  Link2,
  List,
  Pencil,
  Plus,
  Search,
  Star,
  Subtitles,
  Trash2,
  Tv,
  X,
} from 'lucide-react';
import { useState, type CSSProperties, type ReactNode } from 'react';
import { useNavigate } from 'react-router-dom';
import { api } from '../api/client';
import { useSession } from '../app/session';
import { ask } from '../components/Confirm';
import { useAssetUrls } from '../components/EntityForm';
import { Pager, usePaged } from '../components/Pager';
import { Select } from '../components/Select';
import { Empty, ErrorBox, Loading, PageHeader, Toggle } from '../components/ui';
import { Categories, useCategories } from './MediaLibraryPage';

export interface Movie {
  id: string;
  title: string;
  description: string | null;
  year: number | null;
  durationMin: number | null;
  rating: string | null;
  genres: string[];
  audioLanguages: string[];
  subtitleLanguages: string[];
  posterAssetId: string | null;
  backdropAssetId: string | null;
  sourceUrl: string;
  featured: boolean;
  enabled: boolean;
  categories: { categoryId: string }[];
  chapters?: unknown;
  createdAt?: string;
}

/** Full body for PUT /vod/titles/:id (the API replaces the whole title). */
export const movieBody = (m: Movie, patch: Partial<Movie> = {}) => {
  const x = { ...m, ...patch };
  return {
    title: x.title,
    description: x.description,
    year: x.year,
    durationMin: x.durationMin,
    rating: x.rating,
    genres: x.genres,
    audioLanguages: x.audioLanguages,
    subtitleLanguages: x.subtitleLanguages,
    posterAssetId: x.posterAssetId,
    backdropAssetId: x.backdropAssetId,
    sourceUrl: x.sourceUrl,
    featured: x.featured,
    enabled: x.enabled,
    categoryIds: x.categories.map((c) => c.categoryId),
    ...(Array.isArray(x.chapters) ? { chapters: x.chapters } : {}),
  };
};

/** Where the file is played from, its name and format, read from the URL. */
export function sourceInfo(u: string) {
  try {
    const url = new URL(u);
    const h = url.hostname;
    const local = h === 'localhost' || /^(10\.|127\.|192\.168\.|172\.(1[6-9]|2\d|3[01])\.)/.test(h) || !h.includes('.');
    const file = decodeURIComponent(url.pathname.split('/').pop() || '') || '—';
    const ext = file.split('.').pop()?.toLowerCase();
    const format = ext === 'mp4' || ext === 'm4v' ? 'MP4 (H.264/AAC)' : ext === 'm3u8' ? 'HLS' : ext === 'mkv' ? 'MKV' : ext ? ext.toUpperCase() : '—';
    const origin = location.hostname === h ? 'This server' : local ? 'Local server' : h.replace(/^www\./, '');
    return { origin, file, format, local: local || location.hostname === h };
  } catch {
    return { origin: '—', file: '—', format: '—', local: false };
  }
}

function StatCard({ icon, tone, value, label, sub }: { icon: ReactNode; tone: string; value: number; label: string; sub?: ReactNode }) {
  return (
    <div className="stat-tile">
      <span className={`stat-tile-icon ${tone}`}>{icon}</span>
      <span>
        <span className="stat-tile-label">{label}</span>
        <span className="stat-tile-value">{value}</span>
        {sub && <span className="stat-tile-sub">{sub}</span>}
      </span>
    </div>
  );
}

export function VodPage() {
  const [tab, setTab] = useState<'movies' | 'categories'>('movies');
  const navigate = useNavigate();
  const { can } = useSession();
  return (
    <>
      <PageHeader
        title="VOD – Movies"
        sub="Movies played from the hotel server, NAS or Edge (MP4 H.264/AAC). Shared by all sites; packages decide who sees what."
        actions={
          can('ADMIN') && (
            <button className="btn primary" onClick={() => navigate('/content/vod/new')}>
              <Plus size={16} /> Add movie
            </button>
          )
        }
      />
      <Movies tab={tab} setTab={setTab} />
    </>
  );
}

function Movies({ tab, setTab }: { tab: 'movies' | 'categories'; setTab: (t: 'movies' | 'categories') => void }) {
  const { can } = useSession();
  const qc = useQueryClient();
  const navigate = useNavigate();
  const url = useAssetUrls();
  const cats = useCategories('vod');
  const query = useQuery({ queryKey: ['vod', 'items'], queryFn: () => api.get<{ items: Movie[] }>('/vod/titles') });
  const invalidate = () => qc.invalidateQueries({ queryKey: ['vod', 'items'] });
  const put = useMutation({ mutationFn: ({ m, patch }: { m: Movie; patch: Partial<Movie> }) => api.put(`/vod/titles/${m.id}`, movieBody(m, patch)), onSuccess: invalidate });
  const remove = useMutation({
    mutationFn: async (ids: string[]) => {
      for (const id of ids) await api.delete(`/vod/titles/${id}`);
    },
    onSuccess: () => {
      setChecked(new Set());
      return invalidate();
    },
  });
  const bulk = useMutation({
    mutationFn: async (patch: Partial<Movie>) => {
      for (const m of all.filter((x) => checked.has(x.id))) await api.put(`/vod/titles/${m.id}`, movieBody(m, patch));
    },
    onSuccess: invalidate,
  });
  const duplicate = useMutation({ mutationFn: (m: Movie) => api.post('/vod/titles', { ...movieBody(m), title: `${m.title} (copy)`.slice(0, 200), enabled: false, featured: false }), onSuccess: invalidate });
  const [q, setQ] = useState('');
  const [cat, setCat] = useState('all');
  const [status, setStatus] = useState('all');
  const [sort, setSort] = useState('title');
  const [view, setView] = useState<'list' | 'grid'>('list');
  const [selId, setSelId] = useState<string | null>(null);
  const [checked, setChecked] = useState<Set<string>>(new Set());
  const all = query.data?.items ?? [];
  const catList = cats.data?.items ?? [];
  const catName = (id: string) => catList.find((c) => c.id === id)?.name ?? '…';
  const items = all
    .filter((m) => {
      const s = q.trim().toLowerCase();
      if (s && ![m.title, m.description, ...m.genres].some((x) => x?.toLowerCase().includes(s))) return false;
      if (cat !== 'all' && !m.categories.some((c) => c.categoryId === cat)) return false;
      if (status === 'on' && !m.enabled) return false;
      if (status === 'off' && m.enabled) return false;
      if (status === 'featured' && !m.featured) return false;
      return true;
    })
    .sort((a, b) =>
      sort === 'title-desc'
        ? b.title.localeCompare(a.title)
        : sort === 'year-new'
          ? (b.year ?? 0) - (a.year ?? 0)
          : sort === 'year-old'
            ? (a.year ?? 9999) - (b.year ?? 9999)
            : sort === 'added'
              ? (b.createdAt ?? '').localeCompare(a.createdAt ?? '')
              : a.title.localeCompare(b.title),
    );
  const { rows: paged, pager } = usePaged(items, view === 'grid' ? 24 : 10);
  const sel = all.find((m) => m.id === selId) ?? paged[0] ?? null;
  const pageIds = paged.map((m) => m.id);
  const allOnPage = pageIds.length > 0 && pageIds.every((id) => checked.has(id));
  const admin = can('ADMIN');
  const visible = all.filter((m) => m.enabled).length;
  const edit = (m: Movie) => navigate(`/content/vod/${m.id}`);
  const del = (m: Movie) => ask(`Delete ${m.title}? It disappears from the TVs. The video file itself is not touched.`, () => remove.mutate([m.id]));

  return (
    <>
      <div className="stat-strip">
        <StatCard icon={<Film size={22} />} tone="violet" value={all.length} label="Total movies" />
        <StatCard icon={<Star size={22} />} tone="amber" value={all.filter((m) => m.featured).length} label="Featured" sub="In the hero carousel" />
        <StatCard icon={<Layers size={22} />} tone="violet" value={catList.length} label="Categories" />
        <StatCard
          icon={<Tv size={22} />}
          tone="green"
          value={visible}
          label="Visible on TV"
          sub={all.length ? <span style={{ color: '#047857' }}>● {Math.round((visible / all.length) * 100)}% of movies</span> : undefined}
        />
      </div>

      <div className="row" style={{ gap: 8, margin: '16px 0 12px' }}>
        <button className={`pill-tab${tab === 'movies' ? ' on' : ''}`} onClick={() => setTab('movies')}>
          Movies
        </button>
        <button className={`pill-tab${tab === 'categories' ? ' on' : ''}`} onClick={() => setTab('categories')}>
          Categories
        </button>
      </div>

      {tab === 'categories' ? (
        <Categories kind="vod" />
      ) : query.isLoading ? (
        <Loading />
      ) : (
        <div className="fac-layout vod-layout">
          <div className="card fill">
            <div className="filters">
              <label className="search">
                <Search size={16} />
                <input placeholder="Search movies by title, genre, synopsis…" value={q} onChange={(e) => setQ(e.target.value)} />
              </label>
              <Select value={cat} onChange={(e) => setCat(e.target.value)} aria-label="Category">
                <option value="all">All categories</option>
                {catList.map((c) => (
                  <option key={c.id} value={c.id}>
                    {c.name}
                  </option>
                ))}
              </Select>
              <Select value={status} onChange={(e) => setStatus(e.target.value)} aria-label="Status">
                <option value="all">All statuses</option>
                <option value="on">Visible on TV</option>
                <option value="off">Hidden</option>
                <option value="featured">Featured</option>
              </Select>
              <Select value={sort} onChange={(e) => setSort(e.target.value)} aria-label="Sort">
                <option value="title">Title (A–Z)</option>
                <option value="title-desc">Title (Z–A)</option>
                <option value="year-new">Year (newest)</option>
                <option value="year-old">Year (oldest)</option>
                <option value="added">Recently added</option>
              </Select>
              <span className="view-toggle" style={{ marginLeft: 'auto' }}>
                <button className={view === 'list' ? 'on' : ''} onClick={() => setView('list')} title="List">
                  <List size={17} />
                </button>
                <button className={view === 'grid' ? 'on' : ''} onClick={() => setView('grid')} title="Posters">
                  <LayoutGrid size={17} />
                </button>
              </span>
            </div>
            {checked.size > 0 && admin && (
              <div className="bulk-bar" style={{ marginTop: 12 }}>
                <strong>{checked.size} selected</strong>
                <button className="btn small" disabled={bulk.isPending} onClick={() => bulk.mutate({ enabled: true })}>
                  <Eye size={14} /> Show on TV
                </button>
                <button className="btn small" disabled={bulk.isPending} onClick={() => bulk.mutate({ enabled: false })}>
                  <EyeOff size={14} /> Hide
                </button>
                <button className="btn small danger" onClick={() => ask(`Delete ${checked.size} movies? They disappear from the TVs.`, () => remove.mutate([...checked]))}>
                  <Trash2 size={14} /> Delete
                </button>
                <button className="icon-btn" style={{ marginLeft: 'auto' }} onClick={() => setChecked(new Set())} title="Clear selection">
                  <X size={16} />
                </button>
              </div>
            )}
            {items.length === 0 ? (
              <Empty>{all.length ? 'No movie matches.' : 'No movies yet. Use “Add movie”.'}</Empty>
            ) : view === 'grid' ? (
              <div className="poster-grid">
                {paged.map((m) => {
                  const img = url(m.posterAssetId);
                  return (
                    <button key={m.id} type="button" className={`poster-card${sel?.id === m.id ? ' on' : ''}${m.enabled ? '' : ' off'}`} onClick={() => setSelId(m.id)} onDoubleClick={() => admin && edit(m)}>
                      <span className="poster-img">{img ? <img src={img} alt="" /> : <ImageOff size={22} />}</span>
                      {m.featured && <Star size={14} fill="#f59e0b" color="#f59e0b" className="poster-star" />}
                      <strong>{m.title}</strong>
                      <span className="muted">{[m.year, m.durationMin && `${m.durationMin} min`].filter(Boolean).join(' · ')}</span>
                    </button>
                  );
                })}
              </div>
            ) : (
              <div className="table-wrap" style={{ marginTop: 12 }}>
                <table className="fac-table">
                  <thead>
                    <tr>
                      {admin && (
                        <th style={{ width: 34 }}>
                          <input
                            type="checkbox"
                            checked={allOnPage}
                            aria-label="Select this page"
                            onChange={() =>
                              setChecked((c) => {
                                const n = new Set(c);
                                pageIds.forEach((id) => (allOnPage ? n.delete(id) : n.add(id)));
                                return n;
                              })
                            }
                          />
                        </th>
                      )}
                      <th>Title</th>
                      <th>Year</th>
                      <th>Duration</th>
                      <th>Rating</th>
                      <th>Categories</th>
                      <th>Source</th>
                      <th>Featured</th>
                      <th>On TV</th>
                      <th style={{ textAlign: 'right' }}>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {paged.map((m) => {
                      const img = url(m.posterAssetId);
                      const src = sourceInfo(m.sourceUrl);
                      return (
                        <tr key={m.id} className={sel?.id === m.id ? 'selected' : ''} onClick={() => setSelId(m.id)}>
                          {admin && (
                            <td onClick={(e) => e.stopPropagation()}>
                              <input
                                type="checkbox"
                                checked={checked.has(m.id)}
                                aria-label={`Select ${m.title}`}
                                onChange={() =>
                                  setChecked((c) => {
                                    const n = new Set(c);
                                    if (n.has(m.id)) n.delete(m.id);
                                    else n.add(m.id);
                                    return n;
                                  })
                                }
                              />
                            </td>
                          )}
                          <td>
                            <span className="row" style={{ gap: 10, flexWrap: 'nowrap' }}>
                              <span className="fac-thumb poster">{img ? <img src={img} alt="" /> : <ImageOff size={14} />}</span>
                              <strong>{m.title}</strong>
                            </span>
                          </td>
                          <td>{m.year ?? '—'}</td>
                          <td style={{ whiteSpace: 'nowrap' }}>{m.durationMin ? `${m.durationMin} min` : '—'}</td>
                          <td>{m.rating || '—'}</td>
                          <td>
                            <span className="row" style={{ gap: 4 }}>
                              {m.categories.slice(0, 3).map((c) => (
                                <span key={c.categoryId} className="chip">
                                  {catName(c.categoryId)}
                                </span>
                              ))}
                              {m.categories.length > 3 && <span className="muted">+{m.categories.length - 3}</span>}
                            </span>
                          </td>
                          <td className="muted" style={{ whiteSpace: 'nowrap', fontSize: 13 }} title={m.sourceUrl}>
                            <Link2 size={14} style={{ verticalAlign: -2 }} /> {src.origin}
                          </td>
                          <td onClick={(e) => e.stopPropagation()}>
                            <button className={`star-btn${m.featured ? ' on' : ''}`} disabled={!admin} title={m.featured ? 'Remove from featured' : 'Feature it'} onClick={() => put.mutate({ m, patch: { featured: !m.featured } })}>
                              <Star size={17} fill={m.featured ? '#f59e0b' : 'none'} />
                            </button>
                          </td>
                          <td onClick={(e) => e.stopPropagation()}>
                            <Toggle on={m.enabled} onChange={(v) => admin && put.mutate({ m, patch: { enabled: v } })} label="Visible on TV" />
                          </td>
                          <td style={{ textAlign: 'right', whiteSpace: 'nowrap' }} onClick={(e) => e.stopPropagation()}>
                            {admin && (
                              <>
                                <button className="icon-btn" title="Edit" onClick={() => edit(m)}>
                                  <Pencil size={16} />
                                </button>
                                <button className="icon-btn" title="Duplicate (hidden copy)" onClick={() => duplicate.mutate(m)}>
                                  <Copy size={16} />
                                </button>
                                <button className="icon-btn danger-icon" title="Delete" onClick={() => del(m)}>
                                  <Trash2 size={16} />
                                </button>
                              </>
                            )}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
            <Pager {...pager} />
            <ErrorBox error={query.error ?? put.error ?? remove.error ?? bulk.error ?? duplicate.error} />
          </div>

          {sel && <MovieDetail m={sel} url={url} catName={catName} admin={admin} onEdit={() => edit(sel)} onDuplicate={() => duplicate.mutate(sel)} />}
        </div>
      )}
    </>
  );
}

/** Backdrop with a play button; playing swaps it for the video right here (no new tab). */
export function MoviePlayer({ src, image, style }: { src: string | null; image: string | null; style?: CSSProperties }) {
  const [playing, setPlaying] = useState(false);
  const [failed, setFailed] = useState(false);
  if (playing && src)
    return (
      <div className="movie-hero playing" style={style}>
        <video src={src} poster={image ?? undefined} controls autoPlay playsInline onError={() => setFailed(true)} />
        <button type="button" className="movie-close" onClick={() => setPlaying(false)} title="Stop">
          <X size={16} />
        </button>
        {failed && <span className="movie-error">This video cannot be played in the browser. Check the URL and the format (MP4, H.264/AAC).</span>}
      </div>
    );
  return (
    <div className="movie-hero" style={style}>
      {image ? <img src={image} alt="" /> : <Clapperboard size={34} />}
      {src && (
        <button
          type="button"
          className="movie-play"
          title="Play"
          onClick={() => {
            setFailed(false);
            setPlaying(true);
          }}
        >
          ▶
        </button>
      )}
    </div>
  );
}

function MovieDetail({ m, url, catName, admin, onEdit, onDuplicate }: { m: Movie; url: (id: unknown) => string | null; catName: (id: string) => string; admin: boolean; onEdit: () => void; onDuplicate: () => void }) {
  const back = url(m.backdropAssetId) ?? url(m.posterAssetId);
  const src = sourceInfo(m.sourceUrl);
  const fact = (icon: ReactNode, label: string, value: ReactNode) => (
    <div className="movie-fact">
      {icon}
      <span>
        <span className="muted">{label}</span>
        <strong>{value}</strong>
      </span>
    </div>
  );
  return (
    <div className="card fac-detail">
      <MoviePlayer key={m.id} src={m.sourceUrl} image={back} />
      <div className="row" style={{ gap: 8, marginTop: 14, flexWrap: 'nowrap' }}>
        <h2 style={{ margin: 0, fontSize: 22, minWidth: 0, flex: 1 }}>{m.title}</h2>
        {m.featured && (
          <span className="badge warn" style={{ whiteSpace: 'nowrap' }}>
            <Star size={11} /> Featured
          </span>
        )}
        <span className={`state-pill ${m.enabled ? 'online' : 'offline'}`} style={{ whiteSpace: 'nowrap' }}>
          <span className="state-dot" /> {m.enabled ? 'Visible on TV' : 'Hidden'}
        </span>
      </div>
      <div className="muted" style={{ marginTop: 4 }}>
        {[m.year, m.durationMin && `${m.durationMin} min`, m.rating].filter(Boolean).join(' • ')}
      </div>
      {m.genres.length > 0 && (
        <div className="row" style={{ gap: 6, marginTop: 8 }}>
          {m.genres.map((g) => (
            <span key={g} className="chip">
              {g}
            </span>
          ))}
        </div>
      )}
      {m.description && <p className="muted fac-desc">{m.description}</p>}
      <div className="movie-facts">
        {fact(<CalendarDays size={17} />, 'Year', m.year ?? '—')}
        {fact(<Globe size={17} />, 'Source', src.origin)}
        {fact(<Clock3 size={17} />, 'Duration', m.durationMin ? `${m.durationMin} minutes` : '—')}
        {fact(<FileVideo size={17} />, 'File name', src.file)}
        {fact(<Star size={17} />, 'Rating', m.rating || '—')}
        {fact(<Film size={17} />, 'Video', src.format)}
        {fact(<Languages size={17} />, 'Audio', m.audioLanguages.join(', ') || '—')}
        {fact(<Subtitles size={17} />, 'Subtitles', m.subtitleLanguages.join(', ') || 'None')}
      </div>
      <div className="row" style={{ justifyContent: 'space-between', marginTop: 12 }}>
        <strong>Categories</strong>
      </div>
      <div className="row" style={{ gap: 6, marginTop: 6 }}>
        {m.categories.length ? (
          m.categories.map((c) => (
            <span key={c.categoryId} className="chip">
              {catName(c.categoryId)}
            </span>
          ))
        ) : (
          <span className="muted">None: no package shows it yet.</span>
        )}
      </div>
      {admin && (
        <>
          <button className="btn primary" style={{ width: '100%', justifyContent: 'center', marginTop: 16 }} onClick={onEdit}>
            <Pencil size={16} /> Edit movie
          </button>
          <button className="btn" style={{ width: '100%', justifyContent: 'center', marginTop: 8 }} onClick={onDuplicate}>
            <Copy size={16} /> Duplicate
          </button>
        </>
      )}
    </div>
  );
}
